import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { SUBSCRIBE_DISCOUNT } from "@/data/catalog";
import { getVariantId } from "@/lib/shopify";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const STORAGE_KEY = "babyhemp_cart_v1";
const round2 = (n) => Math.round(n * 100) / 100;

const loadCart = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCart);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // product: { id, name, image, price, compareAt }
  // opts: { qty, sellingPlan: 'onetime'|'subscribe', interval }
  const addItem = (product, opts = {}) => {
    const qty = opts.qty || 1;
    const subscribe = opts.sellingPlan === "subscribe";
    const interval = subscribe ? opts.interval || "4w" : null;
    const basePrice = product.price;
    const price = subscribe ? round2(basePrice * (1 - SUBSCRIBE_DISCOUNT)) : basePrice;
    const compareAt = subscribe ? basePrice : product.compareAt || null;
    const key = `${product.id}-${subscribe ? "sub-" + interval : "onetime"}`;

    setItems((prev) => {
      const idx = prev.findIndex((i) => i.key === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + qty };
        return next;
      }
      return [
        ...prev,
        {
          key,
          id: product.id,
          variantId: getVariantId(product.id),
          name: product.name,
          image: product.image,
          price,
          compareAt,
          sellingPlan: subscribe ? "subscribe" : "onetime",
          interval,
          qty,
        },
      ];
    });
    setOpen(true);
  };

  const updateQty = (key, qty) => {
    if (qty <= 0) return removeItem(key);
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, qty } : i)));
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const clear = () => setItems([]);

  const { count, subtotal, savings } = useMemo(() => {
    let count = 0;
    let subtotal = 0;
    let original = 0;
    items.forEach((i) => {
      count += i.qty;
      subtotal += i.price * i.qty;
      original += (i.compareAt || i.price) * i.qty;
    });
    return {
      count,
      subtotal: round2(subtotal),
      savings: round2(Math.max(0, original - subtotal)),
    };
  }, [items]);

  const value = {
    items,
    open,
    setOpen,
    addItem,
    updateQty,
    removeItem,
    clear,
    count,
    subtotal,
    savings,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
